import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminController, permissionController } from '@/api/generated/endpoints';
import type { AdminResponseDTO } from '@/api/generated/types';
import { setUnauthorizedHandler, unwrap } from '@/api/http';
import { PermissionSet } from './permissions';
import { session } from './session';

interface AuthState {
  /** True once a token exists; profile/permissions may still be loading. */
  signedIn: boolean;
  me: AdminResponseDTO | undefined;
  permissions: PermissionSet;
  /** True while the profile or permissions of a signed-in admin are loading. */
  loading: boolean;
  signIn(token: string): void;
  signOut(): void;
}

const AuthContext = createContext<AuthState | null>(null);

const EMPTY = new PermissionSet([]);

/** Owns the session: token lifecycle, current admin, effective permissions, silent refresh. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => session.token());

  useEffect(() => session.subscribe(setToken), []);

  const signOut = useCallback(() => {
    session.clear();
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    setUnauthorizedHandler(signOut);
  }, [signOut]);

  const me = useQuery({
    queryKey: ['auth', 'me', token],
    enabled: !!token,
    queryFn: async () => unwrap(await adminController.getCurrentAdmin()),
    staleTime: 5 * 60_000,
  });

  const permissions = useQuery({
    queryKey: ['auth', 'permissions', token],
    enabled: !!token,
    queryFn: async () => unwrap(await permissionController.myPermissions()),
    staleTime: 5 * 60_000,
  });

  // Exchange an old token for a fresh one; the gateway rotates it in the same session slot.
  useEffect(() => {
    if (!token || !session.needsRefresh()) return;
    adminController
      .getRefreshToken()
      .then((r) => {
        const fresh = unwrap(r)?.token;
        if (fresh) session.set(fresh);
      })
      .catch(() => {
        /* keep the current token; a 401 later signs out */
      });
  }, [token]);

  const value = useMemo<AuthState>(
    () => ({
      signedIn: !!token,
      me: me.data,
      permissions: permissions.data ? new PermissionSet(permissions.data.permissionKeys ?? []) : EMPTY,
      loading: !!token && (me.isPending || permissions.isPending),
      signIn: (t: string) => session.set(t),
      signOut,
    }),
    [token, me.data, me.isPending, permissions.data, permissions.isPending, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
